/**
 * Shared helpers for website form validation and URL normalization.
 */

import { checkSuggestionUrlBoth } from "@/services/websiteSuggestion.service";

export type WebsiteFormData = {
  title: string;
  url: string;
  description: string;
  address: string;
  zipCode: string;
  category1Id: string | null;
  category1Name: string;
  category1Depth?: number | null;
  categoryPathIds: string[];
  categoryPathNames: string[];
  regionId: string | null;
  regionName: string;
  regionPathIds: string[];
  regionPathNames: string[];
};

export type WebsiteFormFieldErrors = {
  title: boolean;
  url: boolean;
  description: boolean;
};

export type WebsiteFormValidationError = {
  messageKey: string;
  max?: number;
  inputId?: string;
};

export type WebsiteFormValidationResult = {
  ok: boolean;
  fieldErrors: WebsiteFormFieldErrors;
  errors: WebsiteFormValidationError[];
  firstErrorId?: string;
  normalizedUrl?: string;
  urlErrorKey?: string | null;
};

export type WebsiteFormValidationOptions = {
  formData: Pick<
    WebsiteFormData,
    "title" | "url" | "description" | "address" | "zipCode" | "category1Id" | "regionId"
  >;
  ids: {
    title: string;
    url: string;
    description: string;
    address: string;
    zip: string;
    category?: string;
    region?: string;
  };
  max: {
    title: number;
    url: number;
    description: number;
    address: number;
    zip: number;
  };
  required: {
    category: boolean;
    region: boolean;
  };
  errorKeys: {
    requiredFields: string;
    categoryRequired?: string;
    regionRequired?: string;
    titleTooLong: string;
    urlTooLong: string;
    descriptionTooLong: string;
    addressTooLong: string;
    zipTooLong: string;
    urlVerifyFailed: string;
    urlExists: string;
  };
};

export const normalizeUrlKeepPathQuery = (input: string) => {
  const raw = input.trim();
  if (!raw) return raw;

  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  let u: URL;
  try {
    u = new URL(withScheme);
  } catch {
    return withScheme.split("#")[0].trim();
  }

  let host = (u.hostname || "").toLowerCase();
  if (host.startsWith("www.")) host = host.slice(4);

  const path = u.pathname || "";
  const query = u.search || "";
  return `https://${host}${path}${query}`;
};

export const validateAndPrepareWebsiteForm = async (
  options: WebsiteFormValidationOptions
): Promise<WebsiteFormValidationResult> => {
  const { formData, ids, max, required, errorKeys } = options;

  const titleEmpty = !formData.title.trim();
  const urlEmpty = !formData.url.trim();
  const descriptionEmpty = !formData.description.trim();

  const fieldErrors: WebsiteFormFieldErrors = {
    title: titleEmpty,
    url: urlEmpty,
    description: descriptionEmpty,
  };

  if (titleEmpty || urlEmpty || descriptionEmpty) {
    const firstErrorId = titleEmpty
      ? ids.title
      : urlEmpty
        ? ids.url
        : ids.description;

    return {
      ok: false,
      fieldErrors,
      errors: [
        {
          messageKey: errorKeys.requiredFields,
          inputId: firstErrorId,
        },
      ],
      firstErrorId,
      urlErrorKey: null,
    };
  }

  if (formData.title.length > max.title) {
    return {
      ok: false,
      fieldErrors,
      errors: [
        {
          messageKey: errorKeys.titleTooLong,
          max: max.title,
          inputId: ids.title,
        },
      ],
      firstErrorId: ids.title,
      urlErrorKey: null,
    };
  }

  if (formData.url.length > max.url) {
    return {
      ok: false,
      fieldErrors,
      errors: [
        {
          messageKey: errorKeys.urlTooLong,
          max: max.url,
          inputId: ids.url,
        },
      ],
      firstErrorId: ids.url,
      urlErrorKey: null,
    };
  }

  if (formData.description.length > max.description) {
    return {
      ok: false,
      fieldErrors,
      errors: [
        {
          messageKey: errorKeys.descriptionTooLong,
          max: max.description,
          inputId: ids.description,
        },
      ],
      firstErrorId: ids.description,
      urlErrorKey: null,
    };
  }

  if (formData.address.length > max.address) {
    return {
      ok: false,
      fieldErrors,
      errors: [
        {
          messageKey: errorKeys.addressTooLong,
          max: max.address,
          inputId: ids.address,
        },
      ],
      firstErrorId: ids.address,
      urlErrorKey: null,
    };
  }

  if (formData.zipCode.length > max.zip) {
    return {
      ok: false,
      fieldErrors,
      errors: [
        {
          messageKey: errorKeys.zipTooLong,
          max: max.zip,
          inputId: ids.zip,
        },
      ],
      firstErrorId: ids.zip,
      urlErrorKey: null,
    };
  }

  const hasZip = /^\d{5}$/.test(formData.zipCode.trim());
  const missingCategory = required.category && !formData.category1Id;
  const missingRegion = required.region && !formData.regionId && !hasZip;

  if (missingCategory || missingRegion) {
    const errors: WebsiteFormValidationError[] = [];

    if (missingCategory && errorKeys.categoryRequired) {
      errors.push({
        messageKey: errorKeys.categoryRequired,
        inputId: ids.category,
      });
    }

    if (missingRegion && errorKeys.regionRequired) {
      errors.push({
        messageKey: errorKeys.regionRequired,
        inputId: ids.region,
      });
    }

    return {
      ok: false,
      fieldErrors,
      errors,
      firstErrorId: missingCategory ? ids.category : ids.region,
      urlErrorKey: null,
    };
  }

  const normalizedUrl = normalizeUrlKeepPathQuery(formData.url);

  const check = await checkSuggestionUrlBoth(normalizedUrl);

  if (!check.ok) {
    return {
      ok: false,
      fieldErrors: { ...fieldErrors, url: true },
      errors: [
        {
          messageKey: errorKeys.urlVerifyFailed,
          inputId: ids.url,
        },
      ],
      firstErrorId: ids.url,
      urlErrorKey: errorKeys.urlVerifyFailed,
    };
  }

  if (check.exists) {
    return {
      ok: false,
      fieldErrors: { ...fieldErrors, url: true },
      errors: [
        {
          messageKey: errorKeys.urlExists,
          inputId: ids.url,
        },
      ],
      firstErrorId: ids.url,
      urlErrorKey: errorKeys.urlExists,
    };
  }

  return {
    ok: true,
    fieldErrors,
    errors: [],
    normalizedUrl,
    urlErrorKey: null,
  };
};
